import { useEffect } from 'react';
import { ApolloCache, useMutation } from '@apollo/client';
import { useOrderState } from '@hooks/useOrderState';
import { OrderDetail, Product } from '@interfaces';
import { useDebouncedCallback } from '@hooks/useDebouncedCallback';
import { useDeepEffect } from '@hooks/useDeepEffect';
import {
  updateProductInOrder,
  UpdateProductInOrderMutationParams,
  UpdateProductInOrderMutationResponse,
} from '@queries/product/mutations';
import { useCallback, useMemo, useRef, useState } from 'react';
import {
  deleteProductInOrder,
  DeleteProductInOrderMutationParams,
  DeleteProductInOrderMutationResponse,
} from '@queries/cart/mutations';
import { useTagManagerEvent } from '@hooks/useTagManagerEvent';
import { Console } from '@utils';
import { useCartMenu } from '@hooks/useCartMenu';
import { usePermissions } from '@hooks/usePermissions';
import { useEventTracking } from '@hooks/useEventTracking';
import {
  ProductInteractionLocation,
  ProductEventType,
} from '@validators/Events/Product';

interface useUpdateQuantityParams {
  product: Product;
  price?: number;
  location: ProductInteractionLocation;
  index?: number;
  openRemoveModal?: () => void;
}

export function useUpdateQuantity({
  product,
  price,
  location,
  index,
}: useUpdateQuantityParams) {
  const { track } = useEventTracking();
  const { userCan } = usePermissions();
  const isLocalVendor = userCan('LOCAL_VENDOR');

  const {
    startLoading: startCartMenuLoading,
    stopLoading: stopCartMenuLoading,
    stopCartMenuRef,
  } = useCartMenu();
  const { addEcommerceEvent } = useTagManagerEvent();
  const { orderUuid, order: orderFromState } = useOrderState();

  const [quantity, setQuantity] = useState(0);

  const productUuid = product?.uuid;
  const MAX_AMOUNT = isLocalVendor
    ? product?.availability?.stock ?? 0
    : userCan('MANAGE_USER')
    ? Number.MAX_SAFE_INTEGER
    : Math.min(
        6,
        product?.discount?.price && product?.discount?.stockRestriction
          ? product?.availability.discountStock
          : ['UNAVAILABLE', 'HIDDEN', 'SOLD_OUT'].includes(
              product?.availability?.statusWithoutStock,
            )
          ? product?.availability?.stock ?? 0
          : product?.availability?.status ===
            product?.availability?.statusWithoutStock
          ? 6
          : ['AVAILABLE', 'REPRESENTATION'].includes(
              product?.availability?.statusWithoutStock,
            )
          ? 6
          : product?.availability?.stock ?? 6,
      );

  const cartOrderDetail = useMemo(() => {
    const currentCart = orderFromState;
    return (
      currentCart?.orderDetails?.find(
        (detail) => detail.product?.uuid === productUuid,
      ) ?? null
    );
  }, [orderFromState, productUuid]);

  const [deleteProduct, { client: apolloClient }] = useMutation<
    DeleteProductInOrderMutationResponse,
    DeleteProductInOrderMutationParams
  >(deleteProductInOrder('productDetail'));

  const [updateQuantity] = useMutation<
    UpdateProductInOrderMutationResponse,
    UpdateProductInOrderMutationParams
  >(updateProductInOrder('addUpdateQuantity'), {
    update(cache, { data: { updateProductInOrder: detail } }) {
      updateCache(detail, cache);
    },
    optimisticResponse(params) {
      return {
        updateProductInOrder: {
          id: cartOrderDetail?.id,
          __typename: 'OrderDetail',
          quantity: params.params.quantity,
          order: orderFromState,
        },
      };
    },
  });

  const currentProductUuidRef = useRef(productUuid);

  useEffect(
    function cartChange() {
      const newQuantity = cartOrderDetail?.quantity;
      setQuantity(newQuantity ?? 0);
    },
    [cartOrderDetail?.quantity],
  );

  useDeepEffect(
    function resetQuantityIfProductChange() {
      if (currentProductUuidRef.current === productUuid) return;
      currentProductUuidRef.current = productUuid;
      setQuantity(cartOrderDetail ? cartOrderDetail.quantity : 0);
    },
    [orderFromState?.orderDetails, productUuid],
  );

  const updateCache = useCallback(
    (
      orderDetail: UpdateProductInOrderMutationResponse['updateProductInOrder'],
      cache: ApolloCache<UpdateProductInOrderMutationResponse> = apolloClient.cache as ApolloCache<UpdateProductInOrderMutationResponse>,
    ) => {
      cache.modify({
        id: cache.identify(orderDetail.order),
        fields: {
          orderDetails(currentOrderDetails: OrderDetail[], { readField }) {
            return currentOrderDetails.map((detail) => {
              if (readField('id', detail) !== orderDetail?.id) return detail;
              return {
                ...detail,
                quantity: orderDetail.quantity,
              };
            });
          },
        },
      });
    },
    [apolloClient.cache],
  );

  const handleUpdateQuantity = useCallback(
    (newQuantity: number) => {
      if (!cartOrderDetail) {
        return -1;
      }
      if (newQuantity <= 0) {
        void deleteProduct({
          variables: {
            params: {
              orderUuid: orderUuid,
              productUuid: cartOrderDetail.product?.uuid,
            },
          },
        }).then(() => {
          setQuantity(0);
        });

        return;
      }
      void updateQuantity({
        variables: {
          params: {
            orderUuid,
            productUuid,
            quantity: newQuantity,
          },
        },
      });
    },
    [cartOrderDetail, deleteProduct, orderUuid, productUuid, updateQuantity],
  );

  const debouncedUpdateQuantity = useDebouncedCallback(
    handleUpdateQuantity,
    500,
  );

  const addQuantity = useCallback(
    (amountToAdd = 1) => {
      startCartMenuLoading();
      const validation = isLocalVendor
        ? quantity < MAX_AMOUNT &&
          product?.availability?.status === 'AVAILABLE_24_HRS'
        : quantity <= MAX_AMOUNT;

      if (validation) {
        try {
          updateCache({
            ...cartOrderDetail,
            quantity: quantity + +amountToAdd,
          });
          setQuantity(+quantity + +amountToAdd);
        } catch (error) {
          clearTimeout(stopCartMenuRef.current);
          stopCartMenuRef.current = setTimeout(() => {
            stopCartMenuLoading();
          }, 2000);
        }
        addEcommerceEvent('add_to_cart', {
          items: [
            {
              item_brand: product?.laboratory,
              item_name: product?.name,
              item_id: String(product?.id),
              price: product?.price,
              quantity: +amountToAdd,
              prescription_type: product?.prescriptionType,
              availability: product?.availability?.status,
            },
          ],
        });
        void track(ProductEventType.ProductAddToCart, {
          product_id: product?.id,
          product_name: product?.name,
          product_price: price ?? product?.price,
          product_prescription_type: product?.prescriptionType ?? null,
          product_brand: product?.makerBrand as string,
          product_availability: product?.availability?.status,
          product_quantity: +quantity + +amountToAdd,
          access: location,
          product_index_position: index ?? null,
          last_whim_type:
            location === 'last_whim' ? product?.lastWhimType : null,
          last_whim_index:
            location === 'last_whim' ? product?.lastWhimIndex : null,
        });
        Console.log({ msg: '[useUpdateQuantity] addQuantity', location });
        void debouncedUpdateQuantity(+quantity + +amountToAdd);
      }
      clearTimeout(stopCartMenuRef.current);
      stopCartMenuRef.current = setTimeout(() => {
        stopCartMenuLoading();
      }, 2000);
    },
    [
      startCartMenuLoading,
      isLocalVendor,
      quantity,
      MAX_AMOUNT,
      product?.availability?.status,
      product?.laboratory,
      product?.name,
      product?.id,
      product?.price,
      product?.prescriptionType,
      product?.makerBrand,
      stopCartMenuRef,
      addEcommerceEvent,
      track,
      price,
      location,
      index,
      debouncedUpdateQuantity,
      updateCache,
      cartOrderDetail,
      stopCartMenuLoading,
    ],
  );

  const subQuantity = useCallback(() => {
    startCartMenuLoading();
    const validation = quantity > 0;
    if (validation) {
      try {
        updateCache({ ...cartOrderDetail, quantity: quantity - 1 });
        setQuantity(quantity - 1);
        void debouncedUpdateQuantity(Math.max(quantity - 1, 0));
      } catch (error) {
        stopCartMenuRef.current = setTimeout(() => {
          stopCartMenuLoading();
        }, 2000);
      }
      addEcommerceEvent('remove_from_cart', {
        items: [
          {
            item_brand: product?.laboratory,
            item_name: product?.name,
            item_id: String(product?.id),
            price: product?.price,
            quantity: 1,
          },
        ],
      });
      void track(ProductEventType.ProductRemoveFromCart, {
        product_id: product?.id,
        product_name: product?.name,
        product_price: product?.price,
        product_prescription_type: product?.prescriptionType ?? null,
        product_brand: product?.brand as string,
        product_availability: product?.availability?.status,
        product_quantity: quantity - 1,
        access: location,
        product_index_position: index ?? null,
        last_whim_type: location === 'last_whim' ? product?.lastWhimType : null,
        last_whim_index:
          location === 'last_whim' ? product?.lastWhimIndex : null,
      });

      Console.log({ msg: '[useUpdateQuantity] subQuantity', location });
    }
    clearTimeout(stopCartMenuRef.current);
    stopCartMenuRef.current = setTimeout(() => {
      stopCartMenuLoading();
    }, 2000);
  }, [
    quantity,
    product?.availability?.status,
    product?.laboratory,
    product?.name,
    product?.id,
    product?.price,
    product?.brand,
    product?.prescriptionType,
    startCartMenuLoading,
    stopCartMenuRef,
    addEcommerceEvent,
    track,
    location,
    index,
    updateCache,
    cartOrderDetail,
    debouncedUpdateQuantity,
    stopCartMenuLoading,
  ]);
  return { addQuantity, subQuantity, quantity, setQuantity, MAX_AMOUNT };
}
